import { useFieldArray, useFormContext } from 'react-hook-form';
import { BoolRadioField } from '@views/register/history/history-fields';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryMiscFormValues } from '@/types/history/history-misc';
import FieldError from '@components/field-error';

interface IProps {
    t: TLocalize;
}

const MedicationsSection = ({ t }: IProps) => {
    const {
        control,
        register,
        watch,
        formState: { errors },
    } = useFormContext<IHistoryMiscFormValues>();
    const { fields, append, remove } = useFieldArray({ control, name: 'medicines' });

    const medication = t('Word_Medication', 'Medication');
    const dosage = t('Word_Dosage', 'Dosage');
    const frequency = t('Word_Frequency', 'Frequency');
    const deleteMedication = t('DashLabel_DeleteMedication', 'Delete medication');

    return (
        <>
            <BoolRadioField
                name="takesMedication"
                label={t('DashLabel_DoYouTakeAnyMedicinesOnARegularBasisIncludingNsaids', "Do you take any medicines on a regular basis (including NSAIDS: Advil, Motrin, Aleve, Naprosyn, Ibuprofen, Goody's Powder)")}
                yesLabel={t('Word_Yes', 'Yes')}
                noLabel={t('Word_No', 'No')}
            />

            {watch('takesMedication') === '1' && (
                <div className="sub-question">
                    <div className="form-group">
                        <div className="col-xs-12">
                            <div className="help-block">
                                {t('DashLabel_PleaseListYourMedicationsDosageAndFrequencyOfUse', 'Please list your medications, dosage, and frequency of use')}
                                <FieldError message={errors.medicines?.root?.message ?? errors.medicines?.message} />
                            </div>

                            <div className="row hidden-xs">
                                <div className="col-xs-4 col-sm-5">{medication}</div>
                                <div className="col-xs-4 col-sm-3">{dosage}</div>
                                <div className="col-xs-4 col-sm-3">{frequency}</div>
                            </div>

                            {fields.map((field, index) => (
                                <div className="row row-margined" key={field.id}>
                                    <div className="col-xs-12 col-sm-5">
                                        <input
                                            className="form-control"
                                            placeholder={medication}
                                            {...register(`medicines.${index}.name`)}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-sm-3">
                                        <input
                                            className="form-control"
                                            placeholder={dosage}
                                            {...register(`medicines.${index}.dose`)}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-sm-3">
                                        <input
                                            className="form-control"
                                            placeholder={frequency}
                                            {...register(`medicines.${index}.frequency`)}
                                        />
                                    </div>
                                    <div className="col-xs-12 col-sm-1">
                                        {index > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                aria-label={deleteMedication}
                                                title={deleteMedication}
                                                onClick={() => remove(index)}
                                            >
                                                <span className="glyphicon glyphicon-trash" aria-hidden="true"></span>
                                            </button>
                                        )}
                                    </div>
                                    <div className="visible-xs">&nbsp;</div>
                                </div>
                            ))}

                            <div className="row">
                                <div className="col-xs-12">
                                    <button
                                        type="button"
                                        className="btn btn-add-entry btn-sm"
                                        style={{ width: 200 }}
                                        onClick={() => append({ name: '', dose: '', frequency: '' })}
                                    >
                                        <span className="glyphicon glyphicon-plus-sign"></span>{' '}
                                        {t('DashLabel_AddMedication', 'Add Medication')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MedicationsSection;
